import { COUNTRY_NAME_EN, COUNTRY_NAME_NL } from '@kamernet/constants/Common';
import {
  City,
  ListingRequest,
  RadiusID,
  RentalPriceID,
  RoomAdvertSortOptionID,
  RoomTypeID,
  Statuses,
  Street,
  SurfaceMinimumID,
} from '@kamernet/core/ApiClient/Kamernet';
import { ServerDataCache } from '@kamernet/core/DataCache/types';
import {
  ListingSearchCityRouteParams,
  ListingSearchStreetRouteParams,
} from '@kamernet/core/Routing';
import {
  LISTING_TYPE_SLUGS_EN,
  LISTING_TYPE_SLUGS_NL,
  LISTING_TYPE_SLUG_TO_ID_MAP,
  ListingTypeSlug,
} from '@kamernet/core/Routing/dynamicRouterMatcher';

import { InvalidLocationError } from '../pages/Listings/errors';

function populateListingTypeIds(
  listingTypeSlug: ListingTypeSlug,
  queryParam: ListingSearchCityRouteParams['listingTypes'],
): Array<RoomTypeID> {
  if (listingTypeSlug === 'properties' || listingTypeSlug === 'huurwoningen') {
    if (!queryParam) {
      return [];
    }

    return queryParam
      .split(',')
      .map(item => parseInt(item, 10))
      .filter(item => item in RoomTypeID);
  }

  return [LISTING_TYPE_SLUG_TO_ID_MAP[listingTypeSlug]];
}

export function populateListingRequestFromUrl(
  serverDataCache: ServerDataCache,
  routeParams: ListingSearchCityRouteParams,
): ListingRequest | undefined {
  if (!routeParams) {
    throw new Error('routeParams is required');
  }

  const streetSlug = (
    routeParams as Partial<ListingSearchStreetRouteParams>
  ).street
    ?.toLowerCase()
    .trim();

  const listingRequest = {} as ListingRequest;
  let citySlug: string | undefined;
  let listingTypeSlug: ListingTypeSlug | undefined;
  let city: City | undefined;
  let street: Street | undefined;
  const typeAndCity = routeParams.typeAndCity.toLowerCase().trim();
  const listingTypeSlugs = [...LISTING_TYPE_SLUGS_EN, ...LISTING_TYPE_SLUGS_NL];

  for (const item of listingTypeSlugs) {
    if (typeAndCity.startsWith(`${item}-`)) {
      citySlug = typeAndCity.replace(`${item}-`, '').trim();
      listingTypeSlug = item;
    }
  }

  if (!citySlug || !listingTypeSlug) {
    throw new InvalidLocationError({
      cause: `City slug can't be pasred`,
      city: typeAndCity,
    });
  }

  if (
    citySlug !== COUNTRY_NAME_EN.toLowerCase() &&
    citySlug !== COUNTRY_NAME_NL.toLowerCase()
  ) {
    city = serverDataCache.cities.find(item => item.slug === citySlug);

    if (!city) {
      throw new InvalidLocationError({
        cause: `City slug doesn't exist`,
        city: citySlug,
      });
    }

    const cityId = city.id;

    if (streetSlug) {
      street = serverDataCache.streets.find(
        item => item.cityId === cityId && item.slug === streetSlug,
      );

      if (!street) {
        throw new InvalidLocationError({
          cause: `Street slug doesn't exist`,
          city: citySlug,
          street: streetSlug,
        });
      }

      listingRequest.citySlug = null;
      listingRequest.streetName = street.name;
      listingRequest.streetSlug = street.slug;
    } else {
      listingRequest.citySlug = city.slug;
      listingRequest.streetName = null;
      listingRequest.streetSlug = null;
    }

    listingRequest.location = {
      cityName: city.name,
      citySlug: city.slug,
      name: city.name,
    };
  } else {
    listingRequest.location = null;
  }

  listingRequest.pageNo = routeParams.pageNo
    ? parseInt(routeParams.pageNo, 10) || 1
    : 1;

  listingRequest.searchview = routeParams.searchview
    ? parseInt(routeParams.searchview, 10) || 1
    : 1;

  listingRequest.listingSortOptionId = routeParams.sort
    ? parseInt(routeParams.sort, 10) || RoomAdvertSortOptionID.NewestFirst
    : RoomAdvertSortOptionID.NewestFirst;

  listingRequest.listingTypeIds = populateListingTypeIds(
    listingTypeSlug,
    routeParams.listingTypes,
  );

  listingRequest.suitableForStatusIds = routeParams.suitableForStatusIds
    ? routeParams.suitableForStatusIds
        .split(',')
        .map(item => parseInt(item, 10))
        .filter(item => item in Statuses)
    : [];

  listingRequest.maxRentalPriceId = routeParams.maxRent
    ? parseInt(routeParams.maxRent, 10) || RentalPriceID.Eur0
    : RentalPriceID.Eur0;

  listingRequest.surfaceMinimumId = routeParams.minSize
    ? parseInt(routeParams.minSize, 10) || SurfaceMinimumID.M6
    : SurfaceMinimumID.M6;

  listingRequest.radiusId = routeParams.radius
    ? parseInt(routeParams.radius, 10) || RadiusID.Km10
    : RadiusID.Km10;

  return listingRequest;
}
